import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import LinkTiles from './LinkTiles';
import { useTranslation } from 'react-i18next';
import { HomeLinkDto, HomeLinkGroupDto } from '../types';
import { isEmptyOrSpaces } from '../../../utils/string-utils';
import { hasPermission } from '../../../auth';
import { useNavigation } from '../../../utils/navigation';
import EnhancedButton from '../../common/EnhancedButton';

type Props = {
  homeLinks: HomeLinkDto[];
  loading?: boolean;
  groups: HomeLinkGroupDto[];
};

/**
 * Renders Home Page contents
 * @param homeLinks
 * @param loading
 * @param groups
 * @constructor
 */
export const HomePage = ({ homeLinks, loading, groups }: Props) => {
  const { t } = useTranslation();
  const { navigateToHomeLinkCreate } = useNavigation();
  const canConfig = hasPermission(['Home.Config']);

  const groupedLinks: Record<string, HomeLinkDto[]> = {};
  const ungroupedLinks: HomeLinkDto[] = [];

  groups.forEach((group) => {
    groupedLinks[group.id] = [];
  });

  homeLinks.forEach((link) => {
    if (isEmptyOrSpaces(link.homeLinkGroupId)) {
      ungroupedLinks.push(link);
    } else {
      groupedLinks[link.homeLinkGroupId as string]?.push(link);
    }
  });

  const hasAnyLinks = homeLinks.length > 0;

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant={'subtitle2'}>{t('links')}</Typography>

        {/* Ungrouped links section */}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap' }}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={`ungrouped-${i}`}
              variant='rectangular'
              width={280}
              height={60}
              sx={{
                marginRight: 1,
                marginBottom: 1,
                borderRadius: 1,
              }}
            />
          ))}
        </Box>

        {/* Generic group sections */}
        {[1, 2, 3].map((groupIndex) => (
          <Box key={`group-${groupIndex}`} sx={{ mt: 2 }}>
            {/* Group header skeleton */}
            <Skeleton
              variant='text'
              width={Math.random() * 100 + 80}
              height={20}
              sx={{ mb: 1 }}
            />

            {/* Group tiles */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={`group-${groupIndex}-tile-${i}`}
                  variant='rectangular'
                  width={280}
                  height={60}
                  sx={{
                    marginRight: 1,
                    marginBottom: 1,
                    borderRadius: 1,
                  }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (!hasAnyLinks) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 6,
          px: 2,
          minHeight: '300px',
        }}
      >
        <Typography
          variant='h5'
          color='text.primary'
          sx={{ mb: 2, fontWeight: 500 }}
        >
          {t('noLinksConfigured')}
        </Typography>

        {canConfig ? (
          <>
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ mb: 3, maxWidth: 400, lineHeight: 1.6 }}
            >
              {t('noLinksConfiguredDescription')}
            </Typography>
            <EnhancedButton
              variant='contained'
              onClick={navigateToHomeLinkCreate}
            >
              {t('createHomeLink')}
            </EnhancedButton>
          </>
        ) : (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ maxWidth: 400, lineHeight: 1.6 }}
          >
            {t('pleaseContactAdminDescription')}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant={'subtitle2'}>{t('links')}</Typography>
      <Box sx={{ mt: 1 }}>
        <LinkTiles homeLinks={ungroupedLinks} />
      </Box>
      {Object.keys(groupedLinks).map((g) => (
        <Box key={g} sx={{ mt: 2 }}>
          <Typography variant={'subtitle2'}>
            {groups.find((grp) => grp.id === g)?.name}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <LinkTiles homeLinks={groupedLinks[g]} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default HomePage;
